"use client"

import { useRef, useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useRouter } from "next/navigation";
import { ArrowRight, Sparkles, Check, X, Play, User, Mic, Send, Square, Loader2, CircleHelp, Copy, CheckCircle, AlertTriangle, XCircle, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { ChartNoAxesColumnIncreasingIcon } from "@/components/ui/chart-no-axes-column-increasing";
import { SparklesIcon } from "@/components/ui/sparkles";
import { UsersIcon } from "@/components/ui/users";
import { FlaskIcon } from "@/components/ui/flask";
import GradientText from "@/components/GradientText";
import Grainient from "@/components/Grainient";
import { STRIPE_BASIC_PRICE_ID, STRIPE_PRO_PRICE_ID } from "@/lib/stripe/config";
import { createClient } from "@/lib/supabase/client";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";

function CopyEmail({ email, className = "" }) {
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(email)
        toast.success("Email address copied to clipboard")
      }}
      className={`inline-flex items-center gap-1 underline underline-offset-4 transition-colors hover:text-foreground ${className}`}
    >
      {email}
      <Copy className="h-3 w-3" />
    </button>
  )
}

const plans = [
  {
    name: "Free",
    price: "$0",
    description: "Practice for free",
    features: [
      { text: "Access to all 350+ questions" },
      { text: "1 live mode interview + feedback" },
      { text: "Unlimited async mode interviews" },
      { text: "Customizable station settings" },
      { text: "View practice history", disabled: true },
      { text: "Unlimited live interviews", disabled: true },
      { text: "Unlimited feedback with percentile grade", disabled: true },
      { text: "Acceptance guarantee", disabled: true },
    ],
    cta: "Get Started",
    href: "/app",
    variant: "outline",
  },
  {
    name: "Basic",
    price: "$129",
    period: "7 days",
    description: "Full access for a week",
    features: [
      { text: "Access to all 350+ questions" },
      { text: "Unlimited interviews in live and async modes" },
      { text: "Unlimited feedback with percentile grade" },
      { text: "Customizable station settings" },
      { text: "View practice history" },
      { text: "Acceptance guarantee" },
      { text: "Lifetime access", disabled: true },
    ],
    cta: "Buy Basic",
    priceEnv: "NEXT_PUBLIC_STRIPE_BASIC_PRICE_ID",
    variant: "outline",
  },
  {
    name: "Pro",
    price: "$159",
    originalPrice: "$349",
    discount: "Save $190",
    period: "lifetime",
    description: "Unlimited access forever",
    features: [
      { text: "Access to all 350+ questions" },
      { text: "Unlimited interviews in live and async modes" },
      { text: "Unlimited feedback with percentile grade" },
      { text: "Customizable station settings" },
      { text: "View practice history" },
      { text: "Acceptance guarantee" },
      { text: "Lifetime access" },
    ],
    cta: "Buy Pro",
    priceEnv: "NEXT_PUBLIC_STRIPE_PRO_PRICE_ID",
    variant: "default",
  },
];

const mmiSchools = [
  // All confirmed MMI schools from scrapedSchoolsDetailed.json
  { src: "https://www.studentdoctor.net/schools-database/img/schools/UBC-FOM.svg?v=2022-05-01 13:34:29", name: "UBC" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/McMaster.svg?v=2025-08-22 20:39:12", name: "McMaster" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/McGill-FOM.svg?v=2022-05-01 12:18:42", name: "McGill" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/DUKEMED.svg?v=2025-08-17 23:24:41", name: "Duke" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/Stanford.svg?v=2025-09-10 21:21:16", name: "Stanford" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/NYU.svg?v=2025-08-31 19:58:00", name: "NYU" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/UMich.png?v=2022-05-01 13:56:32", name: "Michigan" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/Vandy.svg?v=2022-05-01 16:11:31", name: "Vanderbilt" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/WUSTL.svg?v=2022-05-01 16:16:56", name: "WashU" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/UOC-FOM.svg?v=2022-05-01 13:35:08", name: "Calgary" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/UOA-FOM.svg?v=2022-05-01 13:29:36", name: "Alberta" },
  // Reserve pool - cycled in when displayed logos blur out
  { src: "https://www.studentdoctor.net/schools-database/img/schools/QUSM.svg?v=2022-05-01 12:43:58", name: "Queen's" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/DUFM.svg?v=2022-04-30 18:28:36", name: "Dalhousie" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/UC-Davis.svg?v=2025-09-24 17:00:16", name: "UC Davis" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/UCI.svg?v=2025-09-24 17:20:56", name: "UC Irvine" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/UCSD-SOM.svg?v=2025-09-24 18:27:43", name: "UCSD" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/WFUSM.svg?v=2022-05-01 16:14:07", name: "Wake Forest" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/TUSM.svg?v=2025-09-14 18:42:24", name: "Tufts" },
  { src: "https://www.studentdoctor.net/schools-database/img/schools/OHSU-SOM.svg?v=2025-09-06 14:20:53", name: "OHSU" },
];

const wreathFilter = "brightness(0) saturate(100%) invert(60%) sepia(60%) saturate(500%) hue-rotate(10deg) brightness(95%) contrast(90%)";

function Laurel({ children, href }) {
  const goldText = {
    background: "linear-gradient(135deg, #E8C84A 0%, #C9A83A 40%, #E8C84A 70%, #D4B342 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  };

  const inner = (
    <div className={`inline-flex items-center gap-1.5 sm:gap-3 ${href ? "cursor-pointer" : ""}`}>
      <Image
        src="/wreath-left.svg"
        alt=""
        width={26}
        height={52}
        className="h-7 w-auto shrink-0 sm:h-10"
        style={{ filter: wreathFilter }}
      />
      <span className="whitespace-nowrap text-center text-[11px] font-medium leading-snug sm:text-sm" style={goldText}>
        {children}
      </span>
      <Image
        src="/wreath-left.svg"
        alt=""
        width={26}
        height={52}
        className="h-7 w-auto shrink-0 scale-x-[-1] sm:h-10"
        style={{ filter: wreathFilter }}
      />
    </div>
  );

  if (href) {
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </Link>
    );
  }

  return inner;
}

function LoadingLink({ href, onClick, children, ...props }) {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  function handleClick(e) {
    e.preventDefault()
    if (ref.current) {
      ref.current.style.minWidth = `${ref.current.offsetWidth}px`
    }
    setLoading(true)
    if (onClick) onClick()
    router.push(href)
  }

  return (
    <Button ref={ref} {...props} onClick={handleClick} disabled={loading}>
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </>
      ) : children}
    </Button>
  )
}

function HeroCta({ className }) {
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  return (
    <Link
      ref={ref}
      href="/app"
      onClick={() => {
        if (ref.current) ref.current.style.minWidth = `${ref.current.offsetWidth}px`
        setLoading(true)
      }}
      className={`group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-full px-8 text-base font-semibold text-primary-foreground shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl ${loading ? "pointer-events-none" : ""} ${className || ""}`}
      style={{
        background: "linear-gradient(135deg, oklch(0.648 0.2 131.684), oklch(0.723 0.219 149.579), oklch(0.648 0.2 131.684))",
        backgroundSize: "200% 200%",
        animation: "ctaGradient 3s ease infinite, ctaGlow 2s ease-in-out infinite",
      }}
    >
      {/* Shimmer sweep */}
      <span
        className="pointer-events-none absolute inset-0"
        style={{
          background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.3) 50%, transparent 60%)",
          backgroundSize: "200% 100%",
          animation: loading ? "none" : "ctaShimmer 3s ease-in-out infinite",
        }}
      />
      {loading ? (
        <>
          <Loader2 className="relative h-5 w-5 animate-spin" />
          <span className="relative">Loading...</span>
        </>
      ) : (
        <>
          <span className="relative">Start Practicing</span>
          <ArrowRight className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
        </>
      )}
    </Link>
  )
}

function MockupTimer() {
  const [time, setTime] = useState(462) // start at 7:42
  useEffect(() => {
    const id = setInterval(() => setTime((t) => (t <= 0 ? 462 : t - 1)), 1000)
    return () => clearInterval(id)
  }, [])
  const m = Math.floor(time / 60)
  const s = time % 60
  return <span className="font-mono text-2xl text-muted-foreground">{m}:{s.toString().padStart(2, "0")}</span>
}

function AppMockup() {
  const videoRef = useRef(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const resistPause = () => { video.play() }
    video.addEventListener("pause", resistPause)

    return () => {
      video.removeEventListener("pause", resistPause)
    }
  }, [])

  return (
    <div className="relative py-12">
      {/* Glow behind the mockup */}
      <div className="pointer-events-none absolute -inset-8 rounded-3xl bg-[radial-gradient(ellipse_at_center,var(--primary)_0%,transparent_70%)] opacity-[0.1]" />

      {/* Main screen - live interview experience */}
      <div className="relative rounded-xl border bg-card shadow-2xl shadow-primary/5 overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-2.5">
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
            <div className="h-2.5 w-2.5 rounded-full bg-warning/60" />
            <div className="h-2.5 w-2.5 rounded-full bg-primary/60" />
          </div>
          <div className="mx-auto flex h-5 w-48 items-center justify-center rounded-md bg-background text-[10px] text-muted-foreground">
            mmipractice.org/app/station
          </div>
        </div>

        <div className="bg-background p-5">
          {/* Timer */}
          <div className="text-center">
            <MockupTimer />
          </div>

          {/* Two video panels - side by side */}
          <div className="mt-4 flex items-start justify-center gap-6">
            {/* User video */}
            <div className="flex flex-col items-center gap-2">
              <div className="h-28 w-28 overflow-hidden rounded-xl border bg-muted shadow-sm pointer-events-none">
                <video
                  ref={videoRef}
                  src="/maylen.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="h-full w-full object-cover scale-x-[-1]"
                />
              </div>
              <span className="text-[10px] text-muted-foreground">You</span>
              <div className="flex h-5 items-center justify-center gap-[2px]">
                {Array.from({ length: 20 }).map((_, i) => {
                  const center = 10
                  const dist = Math.abs(i - center) / center
                  const maxScale = 1 - dist * 0.6
                  return (
                    <div
                      key={i}
                      className="w-[2px] rounded-full bg-primary origin-center"
                      style={{
                        height: "100%",
                        animation: `mockupWave 0.${3 + (i * 7) % 5}s ease-in-out ${(i * 0.07).toFixed(2)}s infinite alternate`,
                        transform: `scaleY(${(0.15 + maxScale * 0.15).toFixed(2)})`,
                      }}
                    />
                  )
                })}
              </div>
            </div>

            {/* AI interviewer orb */}
            <div className="flex flex-col items-center gap-2">
              <div className="h-28 w-28 overflow-hidden rounded-xl bg-muted shadow-sm">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="relative flex h-16 w-16 items-center justify-center">
                    <div className="absolute inset-0 animate-pulse rounded-full bg-primary/20" />
                    <div className="absolute inset-2 rounded-full bg-primary/15" />
                    <div className="relative h-8 w-8 rounded-full bg-primary/25" />
                  </div>
                </div>
              </div>
              <span className="text-[10px] text-muted-foreground">Interviewer</span>
            </div>
          </div>

          {/* Control bar with buttons */}
          <div className="mt-4 flex items-center justify-center gap-3">
            <div className="flex items-center gap-1.5 rounded-full border bg-destructive/10 px-3 py-1.5 text-destructive">
              <Square className="h-2.5 w-2.5" />
              <span className="text-[10px] font-medium">Stop</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-primary-foreground">
              <Send className="h-2.5 w-2.5" />
              <span className="text-[10px] font-medium">End &amp; Submit</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating bubble - Questions list */}
      <div className="absolute -left-16 top-4 w-56 rotate-[-2deg] rounded-lg border bg-card p-3 shadow-lg">
        <span className="text-[10px] font-semibold text-foreground">350+ Questions</span>
        <div className="mt-2 flex flex-col gap-1.5">
          {[
            { text: "A colleague is consistently late to shifts...", type: "Situational", color: "var(--chart-2)" },
            { text: "Tell me about yourself.", type: "Personal", color: "var(--chart-1)" },
            { text: "Tell me about a time you failed...", type: "Behavioral", color: "oklch(0.62 0.12 250)" },
            { text: "A patient refuses treatment on religious grounds...", type: "Ethics", color: "var(--primary)" },
            { text: "Your hospital must allocate a limited supply of...", type: "Policy", color: "var(--chart-4)" },
            { text: "What are your strengths?", type: "Reflective", color: "oklch(0.68 0.11 300)" },
            { text: "You need to deliver bad news to an elderly patient...", type: "Roleplay", color: "var(--chart-3)" },
          ].map(({ text, type, color }) => (
            <div key={text} className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
              <span className="shrink-0 text-[7px] font-medium text-muted-foreground/50">{type}</span>
              <span className="min-w-0 truncate text-[9px] text-muted-foreground">{text}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1 opacity-50">
            <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/30" />
            <span className="text-[9px] text-muted-foreground">+ 345 more...</span>
          </div>
        </div>
      </div>

      {/* Floating bubble - Percentile grade with bell curve */}
      <div className="absolute -right-12 top-6 w-44 rotate-[2deg] rounded-lg border bg-card p-4 shadow-lg">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] font-semibold text-foreground">Your Ranking</span>
          <span className="text-[8px] text-muted-foreground">Top 10%</span>
        </div>
        {/* Bell curve SVG - full width */}
        <svg viewBox="0 0 140 56" className="mt-2 w-full" aria-hidden="true">
          <defs>
            <linearGradient id="curve-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.25" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.04" />
            </linearGradient>
            <clipPath id="curve-clip">
              <path d="M0,52 Q15,51 28,46 Q42,38 52,24 Q60,12 67,6 Q70,4 72,4 Q74,4 77,6 Q84,12 92,24 Q102,38 116,46 Q129,51 140,52 Z" />
            </clipPath>
          </defs>
          {/* Shaded area under curve up to marker */}
          <rect x="0" y="0" width="112" height="56" clipPath="url(#curve-clip)" fill="url(#curve-fill)" />
          {/* Curve line */}
          <path
            d="M0,52 Q15,51 28,46 Q42,38 52,24 Q60,12 67,6 Q70,4 72,4 Q74,4 77,6 Q84,12 92,24 Q102,38 116,46 Q129,51 140,52"
            fill="none"
            className="stroke-primary/30"
            strokeWidth="1.2"
          />
          {/* Marker line */}
          <line x1="112" y1="8" x2="112" y2="52" className="stroke-primary/50" strokeWidth="1" strokeDasharray="2,2" />
          {/* Glow ring + dot */}
          <circle cx="112" cy="28" r="6" className="fill-primary/10" />
          <circle cx="112" cy="28" r="3" className="fill-primary" />
        </svg>
        {/* Score */}
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-2xl font-bold leading-none text-primary">90</span>
          <sup className="text-[9px] font-semibold text-primary">th</sup>
          <span className="ml-0.5 text-[9px] text-muted-foreground">percentile</span>
        </div>
      </div>

      {/* Floating bubble - Evaluation breakdown */}
      <div className="absolute -left-10 -bottom-6 w-48 rotate-[1deg] rounded-lg border bg-card p-3 shadow-lg">
        <span className="text-[10px] font-semibold text-foreground">Evaluation</span>
        <div className="mt-2 flex flex-col gap-1.5">
          {[
            { label: "Structure", score: 8.5, pct: 85 },
            { label: "Content", score: 9.0, pct: 90 },
            { label: "Delivery", score: 7.2, pct: 72 },
            { label: "Fit", score: 8.0, pct: 80 },
          ].map(({ label, score, pct }) => (
            <div key={label} className="flex items-center gap-2">
              <span className="w-14 text-[9px] text-muted-foreground">{label}</span>
              <div className="h-1.5 flex-1 rounded-full bg-muted">
                <div className="h-1.5 rounded-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[9px] font-semibold text-foreground tabular-nums">{score}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Floating bubble - Transcript */}
      <div className="absolute -right-10 -bottom-8 w-64 rotate-[-1deg] rounded-lg border bg-card p-3 shadow-lg">
        <span className="text-[10px] font-semibold text-foreground">Transcript</span>
        <div className="mt-2 flex gap-3">
          {/* Paused video with scrubber */}
          <div className="w-20 shrink-0 flex flex-col gap-1">
            <div className="relative overflow-hidden rounded-md border bg-muted/40 pointer-events-none">
              <video
                src="/maylen.mp4#t=7"
                preload="metadata"
                playsInline
                muted
                className="aspect-[3/4] w-full object-cover scale-x-[-1]"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-background/80 shadow-sm">
                  <Play className="h-2.5 w-2.5 text-primary ml-[1px]" />
                </div>
              </div>
            </div>
            {/* Scrubber */}
            <div className="flex items-center gap-1.5 px-0.5">
              <div className="h-1 flex-1 rounded-full bg-muted">
                <div className="h-1 w-[52%] rounded-full bg-primary" />
              </div>
              <span className="text-[7px] text-muted-foreground tabular-nums">3:24</span>
            </div>
          </div>
          {/* Transcript lines */}
          <div className="flex flex-1 flex-col divide-y overflow-hidden rounded-md border">
            {[
              { speaker: "Interviewer", text: "How would you handle this situation?", active: false },
              { speaker: "You", text: "I would first consider the patient\u2019s autonomy and right to know...", active: true },
              { speaker: "Interviewer", text: "What about the family\u2019s concerns?", active: false },
            ].map(({ speaker, text, active }, i) => (
              <div key={i} className={`px-2 py-1.5 ${active ? "border-l-2 border-l-primary bg-primary/5" : "border-l-2 border-l-transparent"}`}>
                <span className={`text-[9px] font-semibold ${speaker === "You" ? "text-foreground" : "text-muted-foreground"}`}>
                  {speaker}
                </span>
                <p className="mt-0.5 text-[8px] leading-snug text-muted-foreground line-clamp-2">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function getActiveUsers() {
  const now = new Date()
  const hour = now.getHours() + now.getMinutes() / 60

  // Sine curve peaking at ~21:00 (9 PM), trough at ~9:00 (9 AM)
  const curve = (Math.sin(((hour - 9) / 24) * 2 * Math.PI - Math.PI / 2) + 1) / 2

  // Base range: 104 (trough) to 248 (peak)
  const base = Math.round(104 + curve * 144)

  // Small jitter that changes every 5 minutes (deterministic per 5-min window)
  const fiveMinBlock = Math.floor(now.getTime() / 300000)
  const jitter = ((Math.sin(fiveMinBlock * 127.1) + 1) / 2) * 16 - 8 // ±8
  return Math.round(base + jitter)
}

function PracticingNow() {
  const [count, setCount] = useState(() => getActiveUsers())

  useEffect(() => {
    const id = setInterval(() => setCount(getActiveUsers()), 300000) // update every 5 min
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex items-center gap-2">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <span className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{count}</span> practicing right now
      </span>
    </div>
  )
}

const SLOT_COUNT = 10
const FADE_MS = 600

function LogoFireworks() {
  const [slots, setSlots] = useState(() =>
    Array.from({ length: SLOT_COUNT }, (_, i) => ({ schoolIdx: i, visible: true }))
  )

  const swapCount = useRef(0)

  useEffect(() => {
    const timeouts = []

    const scheduleSwap = (slotIdx) => {
      const wait = (4 + Math.random() * 5) * 1000
      const tid = setTimeout(() => {
        if (swapCount.current >= 2) {
          const retry = setTimeout(() => scheduleSwap(slotIdx), 0)
          timeouts.push(retry)
          return
        }
        swapCount.current++
        setSlots(prev => {
          const next = [...prev]
          next[slotIdx] = { ...next[slotIdx], visible: false }
          return next
        })
        const tid2 = setTimeout(() => {
          setSlots(prev => {
            const shown = new Set(prev.map(s => s.schoolIdx))
            const available = mmiSchools.map((_, i) => i).filter(i => !shown.has(i))
            if (available.length === 0) return prev
            const next = [...prev]
            next[slotIdx] = { schoolIdx: available[Math.floor(Math.random() * available.length)], visible: true }
            return next
          })
          const tid3 = setTimeout(() => {
            swapCount.current--
          }, FADE_MS)
          timeouts.push(tid3)
          scheduleSwap(slotIdx)
        }, FADE_MS)
        timeouts.push(tid2)
      }, wait)
      timeouts.push(tid)
    }

    for (let i = 0; i < SLOT_COUNT; i++) {
      scheduleSwap(i)
    }

    return () => timeouts.forEach(id => clearTimeout(id))
  }, [])

  const row1 = slots.slice(0, 5)
  const row2 = slots.slice(5)

  return (
    <div className="flex flex-col items-center gap-4 overflow-hidden sm:gap-6">
      <div className="flex items-center justify-center gap-6 sm:gap-12">
        {row1.map((slot, i) => (
          <div key={i} className={`flex h-10 w-20 items-center justify-center sm:h-12 sm:w-32 ${i >= 3 ? "hidden sm:flex" : ""}`}>
            <Image
              src={mmiSchools[slot.schoolIdx].src}
              alt={mmiSchools[slot.schoolIdx].name}
              width={128}
              height={48}
              className="max-h-9 w-auto max-w-20 object-contain transition-opacity duration-[600ms] ease-in-out sm:max-h-11 sm:max-w-32"
              style={{ opacity: slot.visible ? 0.85 : 0 }}
              unoptimized
            />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-6 sm:gap-12">
        {row2.map((slot, i) => (
          <div key={i} className={`flex h-10 w-20 items-center justify-center sm:h-12 sm:w-32 ${i >= 3 ? "hidden sm:flex" : ""}`}>
            <Image
              src={mmiSchools[slot.schoolIdx].src}
              alt={mmiSchools[slot.schoolIdx].name}
              width={128}
              height={48}
              className="max-h-9 w-auto max-w-20 object-contain transition-opacity duration-[600ms] ease-in-out sm:max-h-11 sm:max-w-32"
              style={{ opacity: slot.visible ? 0.85 : 0 }}
              unoptimized
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const questionTypes = [
  { id: 1, type: "Personal", color: "var(--chart-1)", question: "Tell me about yourself." },
  { id: 11, type: "Behavioral", color: "oklch(0.62 0.12 250)", question: "Tell me about a time you experienced a team conflict and how you resolved it." },
  { id: 46, type: "Reflective", color: "oklch(0.68 0.11 300)", question: "How will you handle burnout and being overwhelmed?" },
  { id: 52, type: "Situational", color: "var(--chart-2)", question: "What would you do if a patient's family requests you not tell the patient about their diagnosis?" },
  { id: 110, type: "Ethics", color: "var(--primary)", question: "Should students from disadvantaged backgrounds receive preferential admission to health professions programs?" },
  { id: 93, type: "Policy", color: "var(--chart-4)", question: "Do you believe diversity is important in a healthcare setting? Why or why not?" },
  { id: 329, type: "Roleplay", color: "var(--chart-3)", question: "Your friend Alex just texted you asking to meet at a coffee shop. When you arrive, Alex looks like they've been crying. Alex ended a three-year relationship yesterday." },
]

function QuestionBankVisual() {
  const [active, setActive] = useState(0)
  const timerRef = useRef(null)

  const resetTimer = () => {
    clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % questionTypes.length)
    }, 4000)
  }

  useEffect(() => {
    resetTimer()
    return () => clearInterval(timerRef.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (i) => {
    setActive(i)
    resetTimer()
  }

  const current = questionTypes[active]

  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
      <div className="flex min-h-[260px]">
        {/* Type tabs */}
        <div className="flex w-32 shrink-0 flex-col border-r">
          {questionTypes.map((qt, i) => (
            <button
              key={qt.type}
              onClick={() => handleSelect(i)}
              className={`flex items-center gap-2.5 px-4 py-3 text-left text-xs transition-colors ${
                active === i
                  ? "bg-muted/50 font-semibold text-foreground"
                  : "text-muted-foreground hover:bg-muted/30"
              }`}
            >
              <div className={`h-2 w-2 shrink-0 rounded-full ${active === i ? "" : "opacity-40"}`} style={{ backgroundColor: qt.color }} />
              {qt.type}
            </button>
          ))}
        </div>

        {/* Example question */}
        <div className="flex flex-1 flex-col justify-center px-6 py-6">
          <span className="inline-flex w-fit items-center rounded-md px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: `color-mix(in oklch, ${current.color} 10%, transparent)`, color: current.color }}>
            {current.type}
          </span>
          <p className="mt-3 text-sm leading-relaxed text-foreground">
            {current.question}
          </p>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground/50">1 of 50+ {current.type.toLowerCase()} questions</p>
            <Link href={`/app?q=${current.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
              Try this question
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

function LiveInterviewVisual() {
  const [hovered, setHovered] = useState(false)
  const [time, setTime] = useState(342)

  useEffect(() => {
    const id = setInterval(() => setTime((t) => (t <= 0 ? 342 : t - 1)), 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <div
      className="rounded-xl border bg-card shadow-sm overflow-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-destructive" />
          </span>
          <span className="text-[10px] font-medium text-muted-foreground">Live Interview</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[9px] text-muted-foreground/60">Station 3 of 8</span>
          <span className="font-mono text-xs tabular-nums text-muted-foreground">{Math.floor(time / 60)}:{(time % 60).toString().padStart(2, "0")}</span>
        </div>
      </div>

      <div className="flex items-start justify-center gap-10 px-6 py-7">
        {/* You */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full border-2 bg-muted transition-all duration-300"
            style={{
              borderColor: !hovered ? "var(--primary)" : "var(--border)",
              boxShadow: !hovered ? "0 0 0 3px oklch(0.648 0.2 131.684 / 0.15)" : "0 0 0 0px transparent",
            }}
          >
            <User className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="text-[10px] font-medium">You</span>
          <div className="flex items-end justify-center gap-[2px] h-5 w-12">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="w-[3px] rounded-full bg-primary origin-bottom"
                style={{
                  height: "100%",
                  transform: !hovered ? undefined : "scaleY(0.15)",
                  animation: !hovered ? `waveformBar 0.8s ease-in-out ${i * 0.12}s infinite` : "none",
                  transition: "transform 0.3s ease",
                }}
              />
            ))}
          </div>
          <span className="relative h-3 w-16 text-center">
            <span className={`absolute inset-0 text-[9px] font-medium transition-opacity duration-300 text-primary ${!hovered ? "opacity-100" : "opacity-0"}`}>Speaking...</span>
            <span className={`absolute inset-0 text-[9px] font-medium transition-opacity duration-300 text-muted-foreground/40 ${hovered ? "opacity-100" : "opacity-0"}`}>Listening</span>
          </span>
        </div>

        {/* Interviewer */}
        <div className="flex flex-col items-center gap-2">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full border-2 transition-all duration-300"
            style={{
              borderColor: hovered ? "var(--primary)" : "var(--border)",
              boxShadow: hovered ? "0 0 0 3px oklch(0.648 0.2 131.684 / 0.15)" : "0 0 0 0px transparent",
              backgroundColor: hovered ? "oklch(0.648 0.2 131.684 / 0.05)" : "var(--muted)",
            }}
          >
            <Sparkles className={`h-5 w-5 transition-colors duration-300 ${hovered ? "text-primary" : "text-muted-foreground"}`} />
          </div>
          <span className="text-[10px] font-medium">Interviewer</span>
          <div className="flex items-end justify-center gap-[2px] h-5 w-12">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="w-[3px] rounded-full bg-primary origin-bottom"
                style={{
                  height: "100%",
                  transform: hovered ? undefined : "scaleY(0.15)",
                  animation: hovered ? `waveformBar 0.8s ease-in-out ${i * 0.12}s infinite` : "none",
                  transition: "transform 0.3s ease",
                }}
              />
            ))}
          </div>
          <span className="relative h-3 w-16 text-center">
            <span className={`absolute inset-0 text-[9px] font-medium transition-opacity duration-300 text-primary ${hovered ? "opacity-100" : "opacity-0"}`}>Responding...</span>
            <span className={`absolute inset-0 text-[9px] font-medium transition-opacity duration-300 text-muted-foreground/40 ${!hovered ? "opacity-100" : "opacity-0"}`}>Listening</span>
          </span>
        </div>
      </div>

      <div className={`border-t px-4 py-2.5 h-[38px] transition-colors duration-500 ${hovered ? "bg-primary/5" : "bg-muted/20"}`}>
        <div className="relative h-full">
          <div className={`absolute inset-0 flex items-start gap-2 transition-opacity duration-300 ${!hovered ? "opacity-100" : "opacity-0"}`}>
            <Mic className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
            <p className="text-[10px] leading-relaxed italic text-muted-foreground">&ldquo;First, I would have a private conversation with the&hellip;&rdquo;</p>
          </div>
          <div className={`absolute inset-0 flex items-start gap-2 transition-opacity duration-300 ${hovered ? "opacity-100" : "opacity-0"}`}>
            <Sparkles className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
            <p className="text-[10px] leading-relaxed italic text-foreground">&ldquo;What if they responded defensively?&rdquo;</p>
          </div>
        </div>
      </div>
    </div>
  )
}

const feedbackCards = [
  {
    title: "Score",
    content: (() => {
      const pct = 72
      const W = 140, PAD = 8, H = 56 + PAD, baseline = 52 + PAD
      const curvePath = `M0,${baseline} Q15,${51+PAD} 28,${46+PAD} Q42,${38+PAD} 52,${24+PAD} Q60,${12+PAD} 67,${6+PAD} Q70,${4+PAD} 72,${4+PAD} Q74,${4+PAD} 77,${6+PAD} Q84,${12+PAD} 92,${24+PAD} Q102,${38+PAD} 116,${46+PAD} Q129,${51+PAD} ${W},${baseline}`
      const closedPath = curvePath + ` Z`
      const markerX = (pct / 100) * W
      const t = (pct - 50) / 17
      const markerY = baseline - (baseline - (4 + PAD)) * Math.exp(-0.5 * t * t)
      return (
        <div className="flex items-center gap-5">
          <div className="flex shrink-0 flex-col items-center gap-1.5">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-36" aria-hidden="true">
              <defs>
                <linearGradient id="lp-curve-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.04" />
                </linearGradient>
                <clipPath id="lp-curve-clip"><path d={closedPath} /></clipPath>
              </defs>
              <rect x="0" y="0" width={markerX} height={H} clipPath="url(#lp-curve-clip)" fill="url(#lp-curve-fill)" />
              <path d={curvePath} fill="none" className="stroke-primary/30" strokeWidth="1.2" />
              <line x1={markerX} y1={markerY + 6} x2={markerX} y2={baseline} className="stroke-muted-foreground/40" strokeWidth="1" strokeDasharray="2,2" />
              <circle cx={markerX} cy={markerY} r="6" className="fill-primary/10" />
              <circle cx={markerX} cy={markerY} r="3" className="fill-primary" />
            </svg>
            <span className="text-2xl font-bold tracking-tight text-primary">72<span className="text-sm font-semibold align-super">nd</span></span>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">percentile</span>
          </div>
          <p className="text-[10px] leading-relaxed text-muted-foreground">You outperformed 72% of candidates on this question type. Strong ethical reasoning, with room to improve structure.</p>
        </div>
      )
    })(),
  },
  {
    title: "Structure",
    content: (
      <div className="space-y-1.5">
        {[
          { step: "Acknowledge the dilemma", status: "strong", color: "text-primary", evidence: "Identified the tension between confidentiality and family concern" },
          { step: "Explore perspectives", status: "partial", color: "text-yellow-500", evidence: "Discussed patient autonomy but missed the family's emotional needs" },
          { step: "Propose a resolution", status: "missing", color: "text-destructive", evidence: "Jumped to a conclusion without weighing alternatives" },
        ].map((s, i) => (
          <div key={i} className="flex gap-2.5 rounded-lg border bg-card px-3 py-2.5">
            <div className="flex shrink-0 flex-col items-center gap-0.5 pt-0.5">
              {s.status === "strong" ? <CheckCircle className="h-3 w-3 text-primary" /> : s.status === "partial" ? <AlertTriangle className="h-3 w-3 text-yellow-500" /> : <XCircle className="h-3 w-3 text-destructive" />}
              <span className={`text-[8px] font-medium ${s.color}`}>{s.status === "strong" ? "Strong" : s.status === "partial" ? "Partial" : "Missing"}</span>
            </div>
            <div>
              <p className="text-[10px] font-medium text-foreground">{s.step}</p>
              <p className="mt-0.5 text-[9px] text-muted-foreground">{s.evidence}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Substance",
    content: (
      <div>
        <ul className="space-y-2">
          {[
            { icon: "check", text: "Correctly identified patient confidentiality as the core ethical principle at stake." },
            { icon: "check", text: "Demonstrated empathy by acknowledging the emotional difficulty of the situation for all parties." },
            { icon: "tip", text: "Consider addressing the legal obligations that apply here - this is a key competency interviewers look for." },
            { icon: "tip", text: "Your answer would be stronger with a specific example of how you'd facilitate a conversation between the parties." },
          ].map((p, i) => (
            <li key={i} className="flex gap-2 text-[10px]">
              {p.icon === "check"
                ? <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                : <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-yellow-500" />
              }
              <span className="leading-relaxed text-foreground">{p.text}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    title: "Tone",
    content: (
      <div>
        <p className="mb-3 text-[10px] leading-relaxed text-muted-foreground">Your vocal tone shifted noticeably throughout the response, reflecting genuine engagement with the scenario.</p>
        <div className="space-y-1.5">
          {[
            { sentiment: "positive", label: "Strong", description: "Warm, empathetic tone when acknowledging the patient's feelings", color: "text-primary" },
            { sentiment: "negative", label: "Weak", description: "Voice became rushed and uncertain when proposing next steps", color: "text-destructive" },
            { sentiment: "positive", label: "Strong", description: "Calm, measured delivery while explaining confidentiality obligations", color: "text-primary" },
          ].map((m, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border bg-card px-3 py-2">
              <div className="flex shrink-0 flex-col items-center gap-0.5 pt-0.5">
                {m.sentiment === "positive" ? <CheckCircle className={`h-3 w-3 ${m.color}`} /> : <XCircle className={`h-3 w-3 ${m.color}`} />}
                <span className={`text-[8px] font-medium ${m.color}`}>{m.label}</span>
              </div>
              <p className="text-[9px] leading-relaxed text-foreground">{m.description}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Phrasing",
    content: (
      <div>
        <p className="mb-3 text-[10px] leading-relaxed text-muted-foreground">Your language was generally clear, but a few phrases could be more precise and empathetic.</p>
        <table className="w-full text-[9px]" style={{ tableLayout: "fixed" }}>
          <colgroup><col style={{ width: "33%" }} /><col style={{ width: "30%" }} /><col style={{ width: "37%" }} /></colgroup>
          <tbody className="divide-y">
            {[
              { phrase: "I guess I would...", issue: "Sounds uncertain", better: "My approach would be to..." },
              { phrase: "That's not my problem", issue: "Dismissive tone", better: "I understand the concern, and..." },
              { phrase: "You should just...", issue: "Prescriptive, lacks empathy", better: "One option to consider is..." },
            ].map((p, i) => (
              <tr key={i} className="align-top">
                <td className="py-2 pr-2"><span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">{p.phrase}</span></td>
                <td className="py-2 px-1 text-muted-foreground">{p.issue}</td>
                <td className="py-2 pl-1 font-medium text-primary">{p.better}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    title: "Filler Words",
    content: (
      <div>
        <div className="mb-3 flex items-center gap-2">
          <span className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-medium text-muted-foreground">12 total</span>
        </div>
        <div className="mb-3 flex flex-wrap gap-1.5">
          {[
            { word: "um", count: 5 },
            { word: "like", count: 4 },
            { word: "you know", count: 2 },
            { word: "sort of", count: 1 },
          ].map((fw, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-[9px]">
              <span className="font-medium text-foreground">&ldquo;{fw.word}&rdquo;</span>
              <span className="text-muted-foreground">&times;{fw.count}</span>
            </span>
          ))}
        </div>
        <p className="text-[10px] leading-relaxed text-muted-foreground">Your filler word usage is moderate. Most fillers appeared during transitions between ideas, suggesting you may benefit from pausing briefly instead of filling silence. Try replacing &ldquo;um&rdquo; with a short breath.</p>
      </div>
    ),
  },
  {
    title: "Recording",
    content: (
      <div className="flex gap-3">
        <div className="flex-1 rounded-lg border bg-muted/30 overflow-hidden">
          <div className="flex aspect-video items-center justify-center bg-muted/50">
            <div className="flex flex-col items-center gap-1.5 text-muted-foreground/40">
              <Play className="h-6 w-6" />
              <span className="text-[8px] font-medium">Your interview</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-2.5 py-1.5">
            <div className="h-1 flex-1 rounded-full bg-muted">
              <div className="h-1 w-1/3 rounded-full bg-primary" />
            </div>
            <span className="text-[8px] tabular-nums text-muted-foreground">0:34 / 1:52</span>
          </div>
        </div>
        <div className="flex-1 space-y-1.5">
          <p className="text-[8px] font-semibold uppercase tracking-wide text-muted-foreground">Transcript</p>
          {[
            { speaker: "You", time: "0:03", text: "I think the most important principle here is..." },
            { speaker: "Interviewer", time: "0:18", text: "Can you elaborate on that?" },
            { speaker: "You", time: "0:22", text: "Of course. The key consideration is..." },
          ].map((seg, i) => (
            <div key={i} className={`rounded px-2 py-1.5 ${i === 2 ? "border-l-2 border-l-primary bg-primary/5" : ""}`}>
              <div className="flex items-center gap-1.5">
                <span className={`text-[8px] font-semibold ${seg.speaker === "Interviewer" ? "text-muted-foreground" : "text-foreground"}`}>{seg.speaker}</span>
                <span className="font-mono text-[7px] text-muted-foreground/50">{seg.time}</span>
              </div>
              <p className="text-[8px] leading-relaxed text-muted-foreground">{seg.text}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
]

function FeedbackCarousel() {
  const [active, setActive] = useState(0)
  const [maxH, setMaxH] = useState(0)
  const cardsRef = useRef([])
  const timerRef = useRef(null)

  const resetTimer = () => {
    clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % feedbackCards.length)
    }, 5000)
  }

  useEffect(() => {
    resetTimer()
    return () => clearInterval(timerRef.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Render all cards invisibly, measure, keep tallest
  useEffect(() => {
    let tallest = 0
    for (const el of cardsRef.current) {
      if (el) tallest = Math.max(tallest, el.offsetHeight)
    }
    if (tallest > 0) setMaxH(tallest)
  }, [])

  const goTo = (i) => { setActive(i); resetTimer() }
  const current = feedbackCards[active]
  const handleClick = () => { goTo((active + 1) % feedbackCards.length) }

  return (
    <div>
      {/* Invisible cards for measurement */}
      <div className="relative overflow-hidden" style={{ height: 0 }} aria-hidden="true">
        {feedbackCards.map((card, i) => (
          <div key={card.title} ref={(el) => { cardsRef.current[i] = el }} className="absolute left-0 right-0 opacity-0 pointer-events-none">
            <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
              <div className="border-b bg-muted/30 px-4 py-2.5">
                <span className="text-[10px] font-semibold">{card.title}</span>
              </div>
              <div className="px-4 py-4">{card.content}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Fixed-height container */}
      <div
        className="flex cursor-pointer flex-col"
        style={maxH ? { height: `${maxH + 32}px` } : undefined}
        onClick={handleClick}
      >
        <div className="flex flex-1 items-center">
          <div className="w-full overflow-hidden rounded-xl border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2.5">
              <span className="text-[10px] font-semibold text-foreground">{current.title}</span>
              <span className="text-[9px] text-muted-foreground">{active + 1}/{feedbackCards.length}</span>
            </div>
            <div className="px-4 py-4">
              {current.content}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 justify-center gap-1.5 pt-3">
          {feedbackCards.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); goTo(i) }}
              className={`h-1.5 rounded-full transition-all duration-300 ${active === i ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/20 hover:bg-muted-foreground/40"}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function BentoCard({ icon: Icon, children, footer, className }) {
  const iconRef = useRef(null)
  return (
    <Card
      className={`group flex flex-col border-border/50 bg-card/80 backdrop-blur-md transition-colors hover:border-primary/20 ${className}`}
      onMouseEnter={() => iconRef.current?.startAnimation()}
      onMouseLeave={() => iconRef.current?.stopAnimation()}
    >
      <CardContent className="flex h-full flex-col pt-6">
        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon ref={iconRef} size={20} />
        </div>
        {children}
      </CardContent>
      {footer}
    </Card>
  )
}

function PricingSection({ user }) {
  return (
    <section id="pricing" className="">
      <div className="mx-auto max-w-5xl px-6 py-20">
        <div className="text-center">
          <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground/60">Pricing</p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight">
            Practice for free, upgrade to stand out
          </h2>
          <p className="mt-2 text-muted-foreground">
            Get the value of a $5,000 prep course for a fraction of the cost. No subscription, acceptance guaranteed.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative flex flex-col ${plan.discount ? "shadow-lg" : "border-border/50"}`}
              style={plan.discount ? {
                border: "2px solid transparent",
                backgroundImage: "linear-gradient(var(--card), var(--card)), linear-gradient(135deg, oklch(0.648 0.2 131.684), oklch(0.723 0.219 149.579), oklch(0.648 0.2 131.684))",
                backgroundOrigin: "border-box",
                backgroundClip: "padding-box, border-box",
              } : undefined}
            >
              {plan.discount && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge
                    className="text-primary-foreground border-0 px-3 py-1"
                    style={{ background: "linear-gradient(135deg, oklch(0.648 0.2 131.684), oklch(0.723 0.219 149.579))" }}
                  >{plan.discount} - Limited time</Badge>
                </div>
              )}
              <CardContent className="flex flex-1 flex-col pt-6 pb-8">
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  {plan.originalPrice && (
                    <span className="text-lg text-muted-foreground/50 line-through">{plan.originalPrice}</span>
                  )}
                  {plan.period && (
                    <span className="text-sm text-muted-foreground">/ {plan.period}</span>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>
                <ul className="mt-6 flex flex-1 flex-col gap-2">
                  {plan.features.map((f) => (
                    <li key={f.text} className={`flex items-start gap-2 text-sm ${f.disabled ? "text-muted-foreground/40 line-through" : ""}`}>
                      {f.disabled ? (
                        <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
                      ) : (
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      )}
                      {f.text}
                    </li>
                  ))}
                </ul>
                <div>
                  {plan.href ? (
                    <>
                      <LoadingLink variant={plan.variant} className="mt-6 w-full" href={plan.href}>
                        {plan.cta}
                      </LoadingLink>
                      <p className="mt-2 text-center text-xs text-muted-foreground">Never expires</p>
                    </>
                  ) : (
                    <>
                      <LoadingLink
                        variant={plan.variant}
                        className={`mt-6 w-full cursor-pointer ${plan.discount ? "border-0 text-primary-foreground hover:opacity-90" : ""}`}
                        style={plan.discount ? {
                          background: "linear-gradient(135deg, oklch(0.648 0.2 131.684), oklch(0.723 0.219 149.579), oklch(0.648 0.2 131.684))",
                          backgroundSize: "200% 200%",
                          animation: "ctaGradient 3s ease infinite",
                        } : undefined}
                        href={user ? "/checkout" : "/login?returnTo=/checkout"}
                        onClick={() => {
                          const priceId = plan.priceEnv === "NEXT_PUBLIC_STRIPE_BASIC_PRICE_ID"
                            ? STRIPE_BASIC_PRICE_ID
                            : STRIPE_PRO_PRICE_ID;
                          localStorage.setItem("pendingCheckout", JSON.stringify({ priceId }));
                        }}
                      >
                        {plan.cta}
                      </LoadingLink>
                      <p className="mt-2 text-center text-xs text-muted-foreground">Pay once, no subscription</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [user, setUser] = useState(null)

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data }) => {
      setUser(data.user)
    })
  }, [])

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Nav */}
      <SiteNav />

      {/* Hero */}
      <section className="relative min-h-[calc(100svh-49px)] overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,var(--primary)_0%,transparent_50%)] opacity-[0.08]" />
        <div className="mx-auto grid min-h-[calc(100svh-49px)] max-w-7xl items-start gap-10 px-6 py-10 sm:items-center sm:py-14 lg:grid-cols-2 lg:gap-20 lg:px-10">
          {/* Copy - left */}
          <div className="flex min-h-[calc(100svh-49px-5rem)] flex-col items-center justify-between pb-2 text-center sm:min-h-0 sm:justify-start sm:pb-0 lg:items-start lg:text-left">
            {/* Top: Laurel wreaths */}
            <div className="flex flex-col items-center justify-center gap-2 sm:mb-10 sm:flex-row sm:gap-6 lg:justify-start">
              <Laurel>
                <span className="font-bold">Acceptance guarantee</span>
                <br />
                <span className="text-xs font-normal">Keep access until you get in</span>
              </Laurel>
              <Laurel href="https://casperpractice.org">
                From the team behind the
                <br />
                #1 CASPer Test AI Prep site
              </Laurel>
            </div>

            {/* Middle: Pill + Headline + Subheader */}
            <div className="flex flex-col items-center sm:items-center lg:items-start">
              <Badge
                variant="outline"
                className="mb-6 gap-1.5 border-primary/20 px-3 py-1 text-sm text-primary sm:mb-10"
              >
                <Sparkles className="h-3.5 w-3.5" />
                350+ free practice questions
              </Badge>

              <h1 className="text-5xl sm:text-5xl lg:text-[3.75rem] xl:text-7xl font-bold tracking-tight leading-[1.15]">
                <GradientText
                  colors={["oklch(0.648 0.2 131.684)", "oklch(0.871 0.15 154.449)", "oklch(0.723 0.219 149.579)", "oklch(0.648 0.2 131.684)"]}
                  animationSpeed={6}
                  className="text-5xl sm:text-5xl lg:text-[3.75rem] xl:text-7xl font-bold tracking-tight"
                >
                  Rank&nbsp;top&nbsp;10%
                </GradientText>
                {" "}on your MMI
              </h1>

              <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground sm:mt-8 sm:text-lg lg:text-xl">
                Most candidates practice blind. You&apos;ll run realistic MMI stations with an AI that knows what the top 10% do differently - and coaches you to do the same.
              </p>
            </div>

            {/* Bottom: CTAs + Social proof */}
            <div className="flex w-full flex-col items-center sm:items-center lg:items-start">
              <div className="flex w-full flex-col items-center gap-3 sm:mt-12 sm:w-auto sm:flex-row sm:gap-4 lg:items-start">
                <HeroCta className="w-full sm:w-auto" />
                <Button size="lg" variant="ghost" className="h-12 w-full rounded-full px-6 text-base sm:w-auto" asChild>
                  <Link href="#what-you-get">
                    Learn More
                  </Link>
                </Button>
              </div>

              <div className="mt-6 sm:mt-14">
                {/* Mobile: stacked centered layout. Desktop: horizontal row */}
                <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
                  <div className="flex gap-0.5 sm:hidden">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <svg key={i} className="h-3.5 w-3.5 fill-amber-400" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                    ))}
                  </div>
                  <TooltipProvider delayDuration={100}>
                    <div className="flex -space-x-2.5 sm:-space-x-3">
                      {[
                        { src: "/abdullah.png", name: "Abdullah M.", detail: "Medicine · McMaster · 🇨🇦" },
                        { src: "/jana.png", name: "Jana S.", detail: "Medicine · Duke · 🇺🇸" },
                        { src: "/fatima.jpeg", name: "Fatima A.", detail: "PA · University of Manchester · 🇬🇧" },
                        { src: "/pic-jess.png", name: "Jess T.", detail: "Dentistry · Melbourne · 🇦🇺" },
                        { src: "/chris.png", name: "Chris H.", detail: "Medicine · McGill · 🇨🇦" },
                      ].map((student, i) => (
                        <Tooltip key={i}>
                          <TooltipTrigger asChild>
                            <Image
                              src={student.src}
                              alt={student.name}
                              width={40}
                              height={40}
                              className="h-8 w-8 rounded-full border-2 border-background object-cover transition-transform hover:z-10 hover:scale-110 sm:h-10 sm:w-10"
                              style={{ position: "relative" }}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-center">
                            <p className="font-semibold">{student.name}</p>
                            <p className="text-xs text-muted-foreground">{student.detail}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </TooltipProvider>
                  <div className="flex flex-col items-center gap-0.5 sm:items-start sm:gap-1">
                    <div className="hidden gap-0.5 sm:flex">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <svg key={i} className="h-4 w-4 fill-amber-400" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground sm:text-sm"><span className="font-semibold text-foreground">2,400+</span> candidates practiced this cycle</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mockup - right */}
          <div className="relative hidden lg:block">
            <AppMockup />
          </div>
        </div>
      </section>

      {/* School logos trust bar */}
      <section className="pt-4 pb-10">
        <p className="mb-6 text-center text-xs font-medium uppercase tracking-widest text-muted-foreground/60">
          Trusted by applicants to top schools
        </p>
        <LogoFireworks />
      </section>

      {/* Social proof / testimonials - masonry grid */}
      <section>
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight">What applicants are saying</h2>
            <p className="mt-2 text-muted-foreground">Real students. Real results.</p>
          </div>
          <div className="relative mt-14 max-h-[680px] overflow-hidden">
            <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
            {[
              {
                quote: "Such a great resource! I have a busy work schedule so was super useful to have a practice buddy that is always available.",
                name: "Sarah K.",
                detail: "Accepted to McMaster MD",
                avatar: "/sarah.png",
              },
              {
                image: "/reviews/discord-review-1.png",
                name: "nowayjose",
                detail: "Discord · 25/26 Med Application Cycle",
                platform: "discord",
              },
              {
                quote: "I loved casperpractice and mmipractice was even better! Strongly recommend to anyone preparing for MMIs.",
                name: "James L.",
                detail: "Accepted to UBC MD",
              },
              {
                quote: "The roleplay stations were really helpful for getting comfortable talking through scenarios out loud. I kept freezing up before and now I actually have a structure to fall back on. Would recommend to anyone who doesn't know where to start with MMI prep.",
                name: "Priya M.",
                detail: "Accepted to U of T MD",
                avatar: "/priya.png",
              },
              {
                image: "/reviews/reddit-review-1.png",
                name: "MaliceArt",
                detail: "Reddit · r/premedcanada",
                platform: "reddit",
              },
              {
                quote: "This is the only way I was able to effectively practice acting stations - great resource!!",
                name: "Daniel R.",
                detail: "Accepted to McGill MD",
                avatar: "/daniel.png",
              },
              {
                quote: "Used this for about three weeks before my interview and it takes a lot of weight off your shoulders just knowing you've seen every type of question they could throw at you. The ethical scenarios especially helped me think through both sides instead of just jumping to an answer.",
                name: "Emily W.",
                detail: "Accepted to Ottawa MD",
              },
              {
                image: "/reviews/discord-review-2.png",
                name: "Rudah",
                detail: "Discord · 25/26 Med Application Cycle",
                platform: "discord",
              },
            ].map(({ quote, image, name, detail, avatar, platform }) => (
              <Card key={name} className="mb-4 break-inside-avoid border-border/50 transition-colors hover:border-primary/20">
                <CardContent className={image ? "pt-3" : "pt-5"}>
                  {image ? (
                    <Image src={image} alt={`${name} review on ${detail}`} width={400} height={300} className="w-full rounded-md" />
                  ) : (
                    <>
                      <svg className="h-5 w-5 text-muted-foreground/20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.3 2.6C6.1 5.1 3 9.3 3 14c0 3.3 2.4 6 5.4 6 2.6 0 4.6-2 4.6-4.4 0-2.5-1.8-4.3-4.1-4.3-.6 0-1.2.1-1.5.3.8-2.8 3.2-5.6 6.1-7.1L11.3 2.6zm11 0C17.1 5.1 14 9.3 14 14c0 3.3 2.4 6 5.4 6 2.6 0 4.6-2 4.6-4.4 0-2.5-1.8-4.3-4.1-4.3-.6 0-1.2.1-1.5.3.8-2.8 3.2-5.6 6.1-7.1L22.3 2.6z" />
                      </svg>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{quote}</p>
                    </>
                  )}
                  <div className="mt-4 flex items-center gap-3">
                    {platform === "discord" ? (
                      <Image src="/reviews/discord-server-icon.webp" alt="25/26 Med Application Cycle" width={32} height={32} className="h-8 w-8 rounded-full object-cover" />
                    ) : platform === "reddit" ? (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FF4500]">
                        <svg className="h-4.5 w-4.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M11.977 13.79h-1.955l4.549-10.715a.81.81 0 00-.381-1.032C12.447 1.12 10.37.747 8.179 1.18c-3.612.716-6.471 3.68-7.059 7.316a9.01 9.01 0 0010.409 10.377c3.735-.616 6.741-3.635 7.347-7.371.453-2.8-.388-5.405-2.017-7.322a.505.505 0 00-.853.119l-4.029 9.49zM9.98 8.118a1.752 1.752 0 00-1.148.167 1.664 1.664 0 00-.651.596 1.703 1.703 0 00-.258.948v3.96H5.998V6.322h1.876v1.074h.035c.251-.344.567-.628.948-.851a2.55 2.55 0 011.311-.335c.172 0 .335.014.488.042.153.028.267.058.342.09l-.774 1.849a.766.766 0 00-.244-.073z" />
                        </svg>
                      </div>
                    ) : avatar ? (
                      <Image src={avatar} alt={name} width={32} height={32} className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <User className="h-3.5 w-3.5" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold">{name}</p>
                      <p className="text-xs text-muted-foreground">{detail}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-56 bg-gradient-to-t from-background via-background/80 to-transparent" />
          </div>
        </div>
      </section>

      {/* What you'll get */}
      <section id="what-you-get">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="text-center">
            <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground/60">What you&apos;ll get</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight">Everything you need to ace your MMI</h2>
          </div>

          {/* Row 1 - Endless tailored practice */}
          <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h3 className="text-xl font-semibold">Endless curated questions</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                Stop hunting for practice questions. Our bank of 350+ MMI practice questions covers every category you&apos;ll face - ethics, personal, situational, policy, roleplay, and more. Each one is tagged by question type, program, and country so you can drill exactly where you need it. We&apos;re adding new questions regularly, but you&apos;ll never run out.
              </p>
              <Link href="/app" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                Browse all questions
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <QuestionBankVisual />
          </div>

          {/* Row 2 - Realistic practice on demand */}
          <div className="mt-20 grid items-center gap-10 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <LiveInterviewVisual />
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="text-xl font-semibold">Realistic practice on demand</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                The most realistic MMI simulation you&apos;ll find online. Timed prep, a live AI interviewer that listens and responds with follow-up questions, and the same pressure you&apos;ll feel on interview day. Available whenever you are, fully customizable to your MMI format, and completely judgement-free. Practice at 2am in your pajamas if you want.
              </p>
              <Link href="/app" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                Try a free station
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>

          {/* Row 3 - Expertise you can trust */}
          <div className="mt-20 grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h3 className="text-xl font-semibold">Feedback you can trust</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                This isn&apos;t one person&apos;s opinion or a ChatGPT response. Our scoring and feedback are built on extensive research, 100k+ real candidate responses, proprietary AI technology, and a multidisciplinary team with decades of experience in admissions. Beyond evaluating your content and structure, we analyze how you deliver it - your phrasing, filler words, and tone of voice. Every score is calibrated against real peers, and every piece of feedback is something you can act on immediately.
              </p>
              <Link href="/app" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                See how you compare
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <FeedbackCarousel />
          </div>
        </div>
      </section>

      {/* Your competitive advantage - bento grid */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <Grainient
            color1="#FDE4EC"
            color2="#F9A8C9"
            color3="#E8C4F0"
            timeSpeed={0.12}
            contrast={1.05}
            saturation={0.9}
            grainAmount={0.03}
            warpAmplitude={65}
            warpFrequency={3.5}
            warpSpeed={1.2}
            zoom={0.75}
          />
        </div>
        <div className="relative mx-auto max-w-5xl px-6 py-20">
          <div className="text-center">
            <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground/60">Why trust us</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight">Our expertise is your edge</h2>
            <p className="mt-2 max-w-2xl mx-auto text-muted-foreground">
              This isn&apos;t our first rodeo. Here&apos;s what makes us different and what will give you an advantage in your interviews.
            </p>
          </div>

          <div className="mt-14 grid gap-4 sm:grid-cols-2 auto-rows-auto">
            {/* CasperPractice story - hero card, spans 2 rows */}
            <BentoCard
              icon={ChartNoAxesColumnIncreasingIcon}
              className="sm:row-span-2 border-primary/20"
              footer={
                <div className="mt-auto border-t bg-muted/30 px-6 py-5">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <CircleHelp className="h-3.5 w-3.5 text-muted-foreground/50" />
                    What&apos;s CASPer?
                  </p>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                    Created at McMaster University by the same researchers who pioneered the MMI, <Link href="https://takealtus.com/casper/" target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline decoration-muted-foreground/30 underline-offset-2 hover:decoration-foreground">CASPer</Link> has screened 900k+ applicants across 600+ schools on the same core skills -ethics, empathy, communication, and professionalism. It was designed to bring MMI-style assessment earlier in the admissions process, before the interview stage.
                  </p>
                </div>
              }
            >
              <h3 className="font-semibold">Proven approach</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                We built <Link href="https://casperpractice.org" target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline decoration-primary/30 underline-offset-2 hover:decoration-primary">CasperPractice.org</Link>, the #1 AI prep platform for the CASPer test - a situational judgment test directly inspired by the MMI. Everything we learned helping 32,000+ students prepare for CASPer - what works, what doesn&apos;t, and what separates the top scorers - is baked into MMI Practice.
              </p>
              <div className="pt-5 grid grid-cols-3 gap-4">
                <div>
                  <span className="text-4xl font-bold tracking-tight text-primary">#1</span>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">ranked AI CASPer prep</p>
                </div>
                <div>
                  <span className="text-4xl font-bold tracking-tight text-primary">32k+</span>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">students helped</p>
                </div>
                <div>
                  <span className="text-4xl font-bold tracking-tight text-primary">100k+</span>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">responses analyzed</p>
                </div>
              </div>
            </BentoCard>

            {/* Data + AI - merged */}
            <BentoCard icon={SparklesIcon} className="">
              <h3 className="font-semibold">Purpose-built AI</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                We don&apos;t just ask ChatGPT to &ldquo;rate this answer&rdquo;. Our AI was built from the ground up - leveraging hundreds of thousands of real responses, calibrated against proven frameworks, and relying on technology that isn&apos;t available out of the box in consumer AI products.
              </p>
            </BentoCard>

            {/* Team */}
            <BentoCard icon={UsersIcon} className="">
              <h3 className="font-semibold">Experienced team</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                This isn&apos;t a student side project. Our multidisciplinary team has decades of experience in technology, data science, and admissions.
              </p>
              <div className="mt-auto pt-4 flex items-center justify-between">
                {[
                  { src: "https://www.studentdoctor.net/schools-database/img/schools/McGill-FOM.svg?v=2022-05-01 12:18:42", name: "McGill" },
                  { src: "https://www.studentdoctor.net/schools-database/img/schools/UBC-FOM.svg?v=2022-05-01 13:34:29", name: "UBC" },
                  { src: "/udm-logo.png", name: "UdeM" },
                  { src: "/shopify-logo.png", name: "Shopify" },
                  { src: "/pwc-logo.svg", name: "PwC" },
                ].map((org) => (
                  <Image
                    key={org.name}
                    src={org.src}
                    alt={org.name}
                    width={80}
                    height={28}
                    className="h-6 w-auto object-contain opacity-70"
                    unoptimized
                  />
                ))}
              </div>
            </BentoCard>

            {/* Research - full width */}
            <BentoCard icon={FlaskIcon} className="sm:col-span-2">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-8">
                <div className="flex-1">
                  <h3 className="font-semibold">Research-backed</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    Our approach is grounded in published research. Every question, competency, and scoring rubric is informed by peer-reviewed literature - not guesswork.
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:gap-4">
                  <Link href="https://acuityinsights.com/news/updated-casper-test-high-potential-applicants/" target="_blank" rel="noopener noreferrer" className="group/cite flex w-36 flex-col rounded-lg border border-border/80 bg-background px-3 py-2.5 transition-colors hover:border-primary/30 hover:bg-primary/5">
                    <span className="text-[9px] font-medium text-primary/60">Acuity Insights</span>
                    <span className="mt-1 text-[10px] leading-snug text-muted-foreground group-hover/cite:text-foreground">CASPer scorers perform highly on the MMI</span>
                  </Link>
                  <Link href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6695046/" target="_blank" rel="noopener noreferrer" className="group/cite flex w-36 flex-col rounded-lg border border-border/80 bg-background px-3 py-2.5 transition-colors hover:border-primary/30 hover:bg-primary/5">
                    <span className="text-[9px] font-medium text-primary/60">PubMed Central</span>
                    <span className="mt-1 text-[10px] leading-snug text-muted-foreground group-hover/cite:text-foreground">Peer-reviewed SJT scoring methodology</span>
                  </Link>
                </div>
              </div>
            </BentoCard>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="pt-14">
        <div className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[max(24px,calc((100%-64rem)/2+24px))] bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[max(24px,calc((100%-64rem)/2+24px))] bg-gradient-to-l from-background to-transparent" />
          <div className="flex flex-col gap-3 py-2">
            <div className="flex animate-[marquee_35s_linear_infinite] gap-3 w-max">
              {[...Array(3)].map((_, copy) => (
                <div key={copy} className="flex gap-3">
                  {[
                    { objection: "\u201CI\u2019ll just use ChatGPT voice mode\u201D", counter: "No timed stations or real scoring" },
                    { objection: "\u201CMy friend can interview me for free\u201D", counter: "No calibrated feedback" },
                    { objection: "\u201CI was going to do peer practice on Discord\u201D", counter: "You\u2019ll all sound the same at the interview" },
                  ].map(({ objection, counter }) => (
                    <span key={objection} className="group/flip shrink-0 whitespace-nowrap" style={{ perspective: "600px" }}>
                      <span className="relative block transition-transform duration-500 [transform-style:preserve-3d] group-hover/flip:[transform:rotateX(180deg)]">
                        <span className="block rounded-full border border-dashed border-muted-foreground/25 bg-muted/50 px-4 py-1.5 text-sm text-muted-foreground/70 [backface-visibility:hidden]">
                          {objection}
                        </span>
                        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-primary/10 border border-primary/20 px-4 py-1.5 text-sm font-medium text-primary [backface-visibility:hidden] [transform:rotateX(180deg)]">
                          {counter}
                        </span>
                      </span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
            <div className="flex animate-[marquee-reverse_35s_linear_infinite] gap-3 w-max">
              {[...Array(3)].map((_, copy) => (
                <div key={copy} className="flex gap-3">
                  {[
                    { objection: "\u201CI found free questions on Reddit\u201D", counter: "Questions alone don\u2019t improve you" },
                    { objection: "\u201CI\u2019ll just read model answers\u201D", counter: "Reading \u2260 performing live" },
                    { objection: "\u201CSome med student on Discord offered to coach me\u201D", counter: "One opinion \u2260 calibrated scoring" },
                    { objection: "\u201CI budgeted for private tutoring\u201D", counter: "We offer the same value, 90% less" },
                  ].map(({ objection, counter }) => (
                    <span key={objection} className="group/flip shrink-0 whitespace-nowrap" style={{ perspective: "600px" }}>
                      <span className="relative block transition-transform duration-500 [transform-style:preserve-3d] group-hover/flip:[transform:rotateX(180deg)]">
                        <span className="block rounded-full border border-dashed border-muted-foreground/25 bg-muted/50 px-4 py-1.5 text-sm text-muted-foreground/70 [backface-visibility:hidden]">
                          {objection}
                        </span>
                        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-primary/10 border border-primary/20 px-4 py-1.5 text-sm font-medium text-primary [backface-visibility:hidden] [transform:rotateX(180deg)]">
                          {counter}
                        </span>
                      </span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-6 pb-20 pt-10">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight">Free practice could cost you another year</h2>
            <p className="mt-2 max-w-2xl mx-auto text-muted-foreground">
              Generic AI and peer practice can only take you so far. Here&apos;s what you&apos;re missing.
            </p>
          </div>

          <div className="mt-14 overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-4 pr-4 text-left font-medium text-muted-foreground" />
                  <th className="pb-4 px-3 text-center text-xs font-medium text-muted-foreground sm:px-4 sm:text-sm">Peer Practice</th>
                  <th className="pb-4 px-3 text-center text-xs font-medium text-muted-foreground sm:px-4 sm:text-sm">ChatGPT</th>
                  <th className="pb-4 pl-3 text-center text-xs font-semibold text-primary sm:pl-4 sm:text-sm">MMI Practice</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[
                  { feature: "Attentive interviewer with follow-up questions", peer: true, chatgpt: true, us: true },
                  { feature: "Realistic timed stations", peer: true, chatgpt: false, us: true },
                  { feature: "Available 24/7, no scheduling", peer: false, chatgpt: true, us: true },
                  { feature: "Customizable MMI station simulator", peer: false, chatgpt: false, us: true },
                  { feature: "Competency-based scoring rubric", peer: false, chatgpt: false, us: true },
                  { feature: "Percentile ranking vs. peers", peer: false, chatgpt: false, us: true },
                  { feature: "350+ free human-curated MMI questions", peer: false, chatgpt: false, us: true },
                  { feature: "Rewatch recordings with transcript", peer: false, chatgpt: false, us: true },
                  { feature: "Data-driven top-scorer insights", peer: false, chatgpt: false, us: true },
                ].map(({ feature, peer, chatgpt, us }) => (
                  <tr key={feature} className="transition-colors hover:bg-muted/50">
                    <td className="py-3 pr-4 text-xs text-muted-foreground sm:text-sm">{feature}</td>
                    <td className="py-3 px-3 text-center sm:px-4">
                      {peer === true ? <Check className="mx-auto h-4 w-4 text-primary" /> : peer === "partial" ? <span className="text-xs text-muted-foreground">Partial</span> : <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="py-3 px-3 text-center sm:px-4">
                      {chatgpt === true ? <Check className="mx-auto h-4 w-4 text-primary" /> : chatgpt === "partial" ? <span className="text-xs text-muted-foreground">Partial</span> : <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="py-3 pl-3 text-center sm:pl-4">
                      {us === true ? <Check className="mx-auto h-4 w-4 text-primary" /> : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection user={user} />

      {/* FAQ */}
      <section id="faq">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="mt-12 w-full" defaultValue="item-1">
            <AccordionItem value="item-1">
              <AccordionTrigger>How does the acceptance guarantee work?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                With the <strong className="text-foreground">Lifetime plan</strong>, the guarantee is built in -you have access forever, so you can keep practicing until you get accepted. No extra charge, no expiry, no risk of paying for nothing. With the <strong className="text-foreground">Basic plan</strong>, if you don&apos;t get accepted in the cycle you purchased, we&apos;ll give you another free week next cycle. Just forward your rejection email to us from the same email address on your account, and we&apos;ll unlock it.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>What types of questions are included in the question bank?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Our bank of 350+ questions covers the five major question categories that are common among all MMI interviews: personal, situational, ethics, policy, and roleplay. We are adding new questions regularly so stay tuned!
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2b">
              <AccordionTrigger>Do the questions apply to all programs and countries?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                The majority of our questions are general and applicable to any MMI, regardless of your program or country. Some questions are tailored to specific programs (e.g. medicine, dentistry, pharmacy) or countries (e.g. Canada, UK, Australia) - you will see these marked with flag and program icons in the question bank. Even questions tagged for a specific program or country are great practice, since the underlying skills being assessed are the same across all MMIs. That said, certain programs and countries may emphasize different question types or have specific nuances, and we recommend researching what your specific school might include.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger>How do you calculate the percentile score?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Your percentile is calculated using real peer responses from this site and our sister site CasperPractice.org (32,000+ users), since both assessments have a similar format and measure similar core competencies. Responses are calibrated against proven frameworks and we statistically normalize responses across both platforms to estimate how you rank against your peers - not arbitrary scores or AI-generated numbers.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5">
              <AccordionTrigger>Do you use competency frameworks like CanMEDS or AAMC premed competencies?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                We evaluate you on universal healthcare competencies like professionalism, communication, and ethical reasoning, but we don&apos;t score you against a specific framework. Research shows that a more effective approach to the MMI is learning repeatable frameworks for each question type. These frameworks naturally convey the competencies interviewers are measuring - which is a big part of why they work. We teach you these frameworks in the app and evaluate your answers accordingly.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-6">
              <AccordionTrigger>What&apos;s the difference between live and async mode?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                In live mode, you speak to an AI interviewer in real time and it asks follow-up questions - the closest thing to most real MMI stations. In async mode, you record your answer alone after reading the prompt, which is the format some schools use (often the case with Kira Talent interviews). Both modes give you full feedback and scoring.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-7">
              <AccordionTrigger>Can I practice in French?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Yes! You can switch the interview language to French in the station settings before starting a station. The AI interviewer will conduct the entire conversation in French, but your feedback will be delivered in English with French examples.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-8">
              <AccordionTrigger>How do I know the AI feedback won&apos;t just be generic &ldquo;AI slop&rdquo;?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                We test our AI rigorously to ensure it meets quality standards comparable to real human-generated content. We use the latest and most advanced models to power our proprietary AI, and use sophisticated techniques to calibrate our feedback output based on our dataset of real student responses. Try the free interview and see for yourself - we&apos;re confident you&apos;ll be impressed.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-10">
              <AccordionTrigger>Is my data private? Are my recordings stored?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Your privacy is not just an afterthought - it&apos;s a priority. Recordings and transcripts are stored securely and are only accessible to you. We never share your data with schools, other users, or third parties, and we do not watch your videos. Any data we use as part of our scoring system is transcribed, anonymized, and cleansed of any personally identifiable information. This applies to all of our plans, including the free tier. You can delete your recordings at any time.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-10b">
              <AccordionTrigger>I bought the MMIPractice bundle on CasperPractice - how do I access my plan?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {`Your lifetime access is activated automatically - there is nothing to redeem. Just `}<Link href="/login" className="text-primary underline">sign in</Link>{` with the same email you use on CasperPractice (Google or email magic link) and your plan will be unlocked, even if you have never created an MMIPractice account before. If your access doesn't show up, double-check that you signed in with the exact email you used on CasperPractice, or reach out to `}<CopyEmail email="hello@mmipractice.org" className="text-primary hover:text-primary/80" />{` and we'll sort it out.`}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-11">
              <AccordionTrigger>What if I encounter an issue - can I speak to a human?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {`Reach out to us at `}<CopyEmail email="hello@mmipractice.org" className="text-primary hover:text-primary/80" />{` - we usually respond within a few hours.`}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="mx-auto max-w-5xl px-6 py-20 text-center">
          <h2 className="text-2xl font-bold tracking-tight">
            Ready to get an unfair advantage?
          </h2>
          <p className="mt-2 text-muted-foreground">
            350+ questions. AI coaching. Percentile ranking. Start free.
          </p>
          <LoadingLink className="mt-6 rounded-full px-8" size="lg" href="/app">
            Start Practicing
            <ArrowRight className="ml-2 h-4 w-4" />
          </LoadingLink>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
